import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  CreditCard,
  CheckCircle,
  AlertCircle,
  Clock,
  Loader2,
  RefreshCw,
  ExternalLink,
  XCircle,
  PoundSterling,
  Calendar,
  Shield,
  Building
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700'
};

export default function DirectDebitManagement() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [tenancies, setTenancies] = useState([]);
  const [selectedTenancy, setSelectedTenancy] = useState('');
  const [mandateStatus, setMandateStatus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [settingUp, setSettingUp] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  // Check for DD setup completion
  useEffect(() => {
    if (searchParams.get('dd_setup') === 'complete') {
      toast.success('Direct Debit setup complete!');
    }
  }, [searchParams]);
  
  // Fetch tenant's tenancies
  const fetchTenancies = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenancies/tenant`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const tenancyList = data.tenancies || data || [];
        setTenancies(tenancyList);
        if (tenancyList.length > 0 && !selectedTenancy) {
          setSelectedTenancy(tenancyList[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching tenancies:', error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedTenancy]);
  
  // Fetch DD status for selected tenancy
  const fetchMandateStatus = useCallback(async () => {
    if (!selectedTenancy) return;
    
    try {
      const response = await fetch(`${API_URL}/api/direct-debit/status/${selectedTenancy}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMandateStatus(data);
      }
    } catch (error) {
      console.error('Error fetching mandate status:', error);
    }
  }, [selectedTenancy, token]);
  
  // Fetch payment history
  const fetchPayments = useCallback(async () => {
    if (!selectedTenancy) return;
    
    try {
      const response = await fetch(`${API_URL}/api/direct-debit/payments/${selectedTenancy}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPayments(data || []);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  }, [selectedTenancy, token]);
  
  useEffect(() => {
    fetchTenancies();
  }, [fetchTenancies]);
  
  useEffect(() => {
    if (selectedTenancy) {
      fetchMandateStatus();
      fetchPayments();
    }
  }, [selectedTenancy, fetchMandateStatus, fetchPayments]);
  
  // Setup Direct Debit
  const handleSetupDD = async () => {
    setSettingUp(true);
    try {
      const response = await fetch(`${API_URL}/api/direct-debit/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenancy_id: selectedTenancy
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.authorization_url) {
        // Redirect to GoCardless authorization
        window.location.href = data.authorization_url;
      } else {
        toast.error(data.detail || 'Failed to setup Direct Debit');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setSettingUp(false);
    }
  };
  
  // Cancel Direct Debit
  const handleCancelDD = async () => {
    if (!window.confirm('Are you sure you want to cancel your Direct Debit? You will need to set up another payment method.')) {
      return;
    }
    
    setCancelling(true);
    try {
      const response = await fetch(`${API_URL}/api/direct-debit/mandate/${selectedTenancy}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Direct Debit cancelled');
        fetchMandateStatus();
      } else {
        toast.error('Failed to cancel Direct Debit');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setCancelling(false);
    }
  };
  
  const selectedTenancyData = tenancies.find(t => t.id === selectedTenancy);
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="direct-debit-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Direct Debit</h1>
            <p className="text-gray-600">Manage automatic rent payments</p>
          </div>
          <Button variant="outline" onClick={() => { fetchMandateStatus(); fetchPayments(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Tenancy Selector */}
        {tenancies.length > 1 && (
          <Card>
            <CardContent className="py-4">
              <label className="block text-sm font-medium mb-2">Select Tenancy</label>
              <select
                value={selectedTenancy}
                onChange={(e) => setSelectedTenancy(e.target.value)}
                className="w-full border rounded-lg p-3"
                data-testid="tenancy-select"
              >
                {tenancies.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.property?.address_line_1 || t.address} - £{t.current_rent}/month
                  </option>
                ))}
              </select>
            </CardContent>
          </Card>
        )}
        
        {tenancies.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-medium text-gray-600">No Active Tenancies</h3>
              <p className="text-sm text-gray-500 mt-2">
                You need an active tenancy to set up Direct Debit.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Status Card */}
            <Card className={mandateStatus?.direct_debit_active ? 'border-green-200' : 'border-gray-200'}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Direct Debit Status
                  </CardTitle>
                  {mandateStatus?.direct_debit_active ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Active
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-700">
                      Not Set Up
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {mandateStatus?.has_mandate ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <p className="font-medium capitalize">{mandateStatus.status}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Scheme</p>
                        <p className="font-medium uppercase">{mandateStatus.scheme || 'BACS'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Reference</p>
                        <p className="font-medium">{mandateStatus.reference || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Property</p>
                        <p className="font-medium">{selectedTenancyData?.property?.address_line_1 || 'Your Property'}</p>
                      </div>
                    </div>
                    
                    {mandateStatus.direct_debit_active && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-green-800">Automatic Payments Active</p>
                            <p className="text-sm text-green-700">
                              Your rent of £{selectedTenancyData?.current_rent || 0}/month will be collected automatically 
                              on each due date. You'll receive a notification before each payment.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-800 mb-2">Benefits of Direct Debit</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>✓ Never miss a rent payment</li>
                        <li>✓ Protected by the Direct Debit Guarantee</li>
                        <li>✓ Cancel anytime with no penalties</li>
                        <li>✓ Automatic payments on rent due dates</li>
                        <li>✓ Email notifications before each payment</li>
                      </ul>
                    </div>
                    
                    {mandateStatus?.message && (
                      <p className="text-sm text-gray-600">{mandateStatus.message}</p>
                    )}
                  </div>
                )}
              </CardContent>
              <CardFooter className="border-t">
                {mandateStatus?.has_mandate && mandateStatus?.direct_debit_active ? (
                  <Button 
                    variant="destructive"
                    onClick={handleCancelDD}
                    disabled={cancelling}
                    data-testid="cancel-dd-btn"
                  >
                    {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                    Cancel Direct Debit
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSetupDD}
                    disabled={settingUp}
                    className="w-full md:w-auto"
                    data-testid="setup-dd-btn"
                  >
                    {settingUp ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Setting up...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Set Up Direct Debit
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </CardFooter>
            </Card>
            
            {/* Payment History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PoundSterling className="w-5 h-5" />
                  Payment History
                </CardTitle>
                <CardDescription>
                  Recent Direct Debit payments for this tenancy
                </CardDescription>
              </CardHeader>
              <CardContent>
                {payments.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No payment history yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {payments.map((payment) => (
                      <div 
                        key={payment.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                        data-testid={`payment-${payment.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            payment.status === 'paid' ? 'bg-green-100' : 
                            payment.status === 'failed' ? 'bg-red-100' : 'bg-amber-100'
                          }`}>
                            {payment.status === 'paid' ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : payment.status === 'failed' ? (
                              <XCircle className="w-5 h-5 text-red-600" />
                            ) : (
                              <Clock className="w-5 h-5 text-amber-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">£{payment.amount?.toFixed(2)}</p>
                            <p className="text-sm text-gray-500">{payment.description || 'Rent Payment'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={PAYMENT_STATUS_COLORS[payment.status] || 'bg-gray-100'}>
                            {payment.status}
                          </Badge>
                          <p className="text-sm text-gray-500 mt-1">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {payment.charge_date}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Direct Debit Guarantee */}
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="py-4">
                <h4 className="font-medium text-gray-800 mb-2">Direct Debit Guarantee</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• This Guarantee is offered by all banks and building societies that accept instructions to pay Direct Debits.</li>
                  <li>• If there are any changes to the amount, date or frequency of your Direct Debit, the organisation will notify you normally 10 working days in advance of your account being debited.</li>
                  <li>• If an error is made in the payment of your Direct Debit, by the organisation or your bank or building society, you are entitled to a full and immediate refund.</li>
                  <li>• You can cancel a Direct Debit at any time by simply contacting your bank or building society.</li>
                </ul>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
